import { getBottomRightExclusion } from "@/components/layout/bottomRightExclusion";

/**
 * Geometry for the floating chat widget.
 *
 * Kept out of ChatWidget.tsx so that file only exports a component — a module
 * that mixes component and non-component exports breaks React Fast Refresh
 * (react-refresh/only-export-components).
 */

/**
 * Fixed height of the collapsed launcher. Idle, working, and unread-reply
 * states all occupy this exact footprint so status changes never move the
 * bottom-right corner stack.
 */
export const COLLAPSED_HEIGHT = 48;
/** Fixed width of the minimized composer. The open panel keeps its resizable width. */
export const COLLAPSED_WIDTH = 258;
/** Bottom margin from viewport edge */
export const BOTTOM_MARGIN = 12;
/** Top offset to keep widget below the app header */
export const TOP_OFFSET = 56;
/** Nav sidebar widths — must match Tailwind w-16 / w-60 in NavigationSidebar (WHA-3109) */
export const NAV_COLLAPSED_WIDTH = 64;
export const NAV_EXPANDED_WIDTH = 240;
/** Gap between the right edge of the nav sidebar and the chat widget (WHA-3109) */
export const NAV_GAP = 8;
/**
 * Gap from the right viewport edge. Wide enough to clear the right-edge panel
 * tab (issue detail / IFC changes panels) so the chat never sits on top of it.
 */
export const RIGHT_MARGIN = 24;

/**
 * Bottom inset of the bottom-right lane — the first pixel above the collapsed
 * chat pill, plus a small gap. The toaster starts here too (raised further by
 * the lane's published height). See `components/layout/BottomRightLane.tsx`
 * and `components/ui/sonner.tsx`.
 */
export const LANE_BOTTOM = BOTTOM_MARGIN + COLLAPSED_HEIGHT + 8;

/**
 * Trailing space a scrollable page adds *inside* its own scroller so the last
 * row can be scrolled out from under the collapsed chat pill.
 *
 * This used to be `pb-[72px]` on the shared `[data-sidebar-content]` column in
 * `BuildingLayout`. Padding on that element is padding on a 100vh flex column,
 * so every page that owns an inner scroller (the field list, the issues list,
 * the documents table) was squeezed into `100vh - 72px` and the reserve read as
 * a dead band the list could never scroll into — a blank footer covering the
 * bottom of the content. Held here instead, the scroller runs to the window
 * edge and the reserve is the last thing in the scrolled content, where it does
 * the job it was meant to do.
 */
export const CHAT_LAUNCHER_RESERVE = BOTTOM_MARGIN + COLLAPSED_HEIGHT + 12;

/** Bottom-right anchor for a widget of the given width — the chat's home. */
export function bottomRightPosition(width: number): { x: number; y: number } {
  return {
    x: Math.max(
      NAV_COLLAPSED_WIDTH + NAV_GAP,
      window.innerWidth - width - RIGHT_MARGIN - getBottomRightExclusion()
    ),
    y: Math.max(0, window.innerHeight - TOP_OFFSET - COLLAPSED_HEIGHT - BOTTOM_MARGIN),
  };
}

/**
 * Clamp a widget position so the whole widget stays inside the portal
 * container (`fixed`, top: TOP_OFFSET, overflow-hidden — anything outside it
 * is clipped and therefore unclickable). Rnd's `bounds="parent"` only
 * constrains live drags; positions restored from state or computed while the
 * window was a different size must be clamped here.
 */
export function clampToViewport(
  position: { x: number; y: number },
  width: number,
  height: number
): { x: number; y: number } {
  const maxX = Math.max(0, window.innerWidth - width);
  const maxY = Math.max(0, window.innerHeight - TOP_OFFSET - height);
  return {
    x: Math.min(Math.max(0, position.x), maxX),
    y: Math.min(Math.max(0, position.y), maxY),
  };
}
